import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageSquareIcon } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuth';
import { PROJECTS, DELIVERABLES, INVOICES, STATUS_LABELS } from '../../data/portal';
export function PortalDashboard() {
  const {
    user
  } = usePortalAuth();
  if (!user) return null;
  const projects = PROJECTS.filter(project => project.organisationId === user.organisationId);
  const projectIds = projects.map(project => project.id);
  const deliverables = DELIVERABLES.filter(item => projectIds.includes(item.projectId)).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 3);
  const invoices = INVOICES.filter(invoice => invoice.organisationId === user.organisationId);
  const latestInvoice = invoices[0];
  const nextMilestone = projects.flatMap(project => project.milestones.map(milestone => ({
    ...milestone,
    project: project.name
  }))).find(milestone => milestone.status === 'in-progress');
  const latestMessage = projects[0]?.messages[projects[0].messages.length - 1];
  return <div className="space-y-8" data-id="element-1204" data-mp="379czqewm491x">
      <header data-id="element-1205" data-mp="37a4rtfr03ong">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-1206" data-mp="37bpopc0sfjoh">Dashboard</p>
        <h1 className="mt-3 font-display text-[30px] leading-tight text-ink sm:text-[36px]" data-id="element-1207" data-mp="32gtj192moxsw">
          Good day, {user.name.split(' ')[0]}
        </h1>
        <p className="mt-2 text-[16px] text-body" data-id="element-1208" data-mp="2tihurdp8ic3m">
          Here is where {user.organisationName} stands with us today.
        </p>
      </header>

      <section aria-labelledby="active-projects" className="space-y-4" data-id="element-1209" data-mp="2ol811lutbe9z">
        <h2 id="active-projects" className="font-display text-[22px] text-ink" data-id="element-1210" data-mp="2jkkaf1ahwzga">
          Active projects
        </h2>
        {projects.map(project => <article key={project.id} className="rounded-md border border-line bg-white p-6" data-id="element-1211" data-mp="ht77ghs391mv">
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1212" data-mp="ctuav5bf0g73">
              <div data-id="element-1213" data-mp="crrmsqttox50">
                <p className="text-small uppercase tracking-[0.12em] text-muted" data-id="element-1214" data-mp="27a2q27l0h7by">{project.serviceLine}</p>
                <h3 className="mt-2 font-display text-[21px] leading-snug text-ink" data-id="element-1215" data-mp="2c11b1kgy2tlj">
                  <Link to={`/portal/projects/${project.id}`} className="underline-offset-4 hover:underline" data-id="element-1216" data-mp="1xbwq3virmb9q">
                    {project.name}
                  </Link>
                </h3>
              </div>
              <span className="rounded-full bg-champagne px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink" data-id="element-1217" data-mp="2pzskuuorxt3v">
                {STATUS_LABELS[project.status]}
              </span>
            </div>

            <div className="mt-5" data-id="element-1218" data-mp="2gykmydq66uab">
              <div className="flex items-baseline justify-between text-small text-muted" data-id="element-1219" data-mp="2gzcg7hwaofxf">
                <span data-id="element-1220" data-mp="2kzeah5zpa72o">Progress</span>
                <span data-id="element-1221" data-mp="15kgx9jqm7sb2">{project.progress}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-mist" data-id="element-1222" data-mp="15j5rjedm0ahb">
                <div className="h-full rounded-full bg-gold" style={{
              width: `${project.progress}%`
            }} data-id="element-1223" data-mp="19nlw9cbhlbuy" />
              </div>
            </div>

            <Link to={`/portal/projects/${project.id}`} className="mt-5 inline-flex items-center gap-2 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-1224" data-mp="x3gil4epvozy">
              Open project
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1225" data-mp="172xn3ii8fwuc" />
            </Link>
          </article>)}
      </section>

      <div className="grid gap-6 lg:grid-cols-2" data-id="element-1226" data-mp="j9xo9ukkzmil">
        <section aria-labelledby="next-milestone" className="rounded-md border border-line bg-white p-6" data-id="element-1227" data-mp="edqgrxcje2yj">
          <h2 id="next-milestone" className="font-display text-[20px] text-ink" data-id="element-1228" data-mp="t2wknymbkhcr">
            Next milestone
          </h2>
          {nextMilestone ? <>
              <p className="mt-4 font-display text-[19px] leading-snug text-ink" data-id="element-1229" data-mp="lafb0zaaczq">{nextMilestone.name}</p>
              <p className="mt-2 text-[15px] text-body" data-id="element-1230" data-mp="3rsdb65q2h7ga">{nextMilestone.project}</p>
              <p className="mt-3 text-small text-muted" data-id="element-1231" data-mp="2v0gg30gr2ca8">
                Due{' '}
                {new Date(nextMilestone.due).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              </p>
            </> : <p className="mt-4 text-[15px] text-body" data-id="element-1232" data-mp="38s4rt2n36yqo">No milestone is currently in progress.</p>}
        </section>

        <section aria-labelledby="latest-invoice" className="rounded-md border border-line bg-white p-6" data-id="element-1233" data-mp="3dqg37ht15hb0">
          <h2 id="latest-invoice" className="font-display text-[20px] text-ink" data-id="element-1234" data-mp="3io7uqaprj1eo">
            Latest invoice
          </h2>
          {latestInvoice ? <>
              <p className="mt-4 font-display text-[26px] text-royal" data-id="element-1235" data-mp="3rpo5ijkhp5jf">
                ${latestInvoice.amount.toLocaleString('en-GB')}
              </p>
              <p className="mt-2 text-[15px] text-body" data-id="element-1236" data-mp="1sfzk7r1ue3hj">
                {latestInvoice.number}, due{' '}
                {new Date(latestInvoice.dueDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long'
            })}
              </p>
              <Link to="/portal/invoices" className="mt-4 inline-block text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-1237" data-mp="1l0dtg1x8t6sf">
                View all invoices
              </Link>
            </> : <p className="mt-4 text-[15px] text-body" data-id="element-1238" data-mp="28rj7qcnjbr0m">There are no invoices on your account.</p>}
        </section>
      </div>

      <section aria-labelledby="recent-deliverables" className="rounded-md border border-line bg-white p-6" data-id="element-1239" data-mp="1ytvbtje70dgv">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1240" data-mp="1usjus29dx0ka">
          <h2 id="recent-deliverables" className="font-display text-[20px] text-ink" data-id="element-1241" data-mp="1uucusyphsmjb">
            Recent deliverables
          </h2>
          <Link to="/portal/deliverables" className="text-[15px] text-royal underline-offset-4 hover:underline" data-id="element-1242" data-mp="jclhfm76bsd9">
            All deliverables
          </Link>
        </div>
        <ul className="mt-5 divide-y divide-line" data-id="element-1243" data-mp="ksfsp1dhgh4t">
          {deliverables.map(item => <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-id="element-1244" data-mp="pr0s4ykwuws7">
              <div data-id="element-1245" data-mp="aygq619hyvcc">
                <p className="text-[15px] text-ink" data-id="element-1246" data-mp="fuxgn2465lsl">{item.name}</p>
                <p className="mt-0.5 text-small text-muted" data-id="element-1247" data-mp="fxa6s2k2z2gq">
                  {item.type} · {item.version} ·{' '}
                  {new Date(item.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long'
              })}
                </p>
              </div>
            </li>)}
        </ul>
      </section>

      {latestMessage && <section aria-labelledby="team-message" className="rounded-md border border-line bg-champagne p-6" data-id="element-1248" data-mp="3t9aj3hd3g920">
          <div className="flex items-center gap-2 text-gold" data-id="element-1249" data-mp="3fbdk6eb63qk6">
            <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-1250" data-mp="3jbwdkcspl4oh" />
            <h2 id="team-message" className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1251" data-mp="3jb4gexjgqyw3">
              From the team
            </h2>
          </div>
          <p className="mt-4 text-[16px] leading-relaxed text-body" data-id="element-1252" data-mp="2vpajxttat0fn">{latestMessage.text}</p>
          <p className="mt-3 text-small text-muted" data-id="element-1253" data-mp="39ok3n3gpqcod">
            {latestMessage.from},{' '}
            {new Date(latestMessage.at).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long'
        })}
          </p>
        </section>}
    </div>;
}